import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../config";
import { Activity } from "lucide-react";

/**
 * V232 — Latest Audits Ticker (horizontal rolling marquee).
 *
 * Pulls the N most-recent firm / city audits from
 * /api/stats/latest-audits and animates them across the viewport.
 * Each entry is wrapped in a real <a> that deep-links to the generated
 * programmatic SEO page — turning the "live proof" strip into an internal
 * link farm that boosts PageRank flow to deep programmatic pages.
 *
 * Behaviour:
 *   - Auto-refreshes every 60s (matches backend _LATEST_TTL_SEC).
 *   - CSS-only infinite scroll (duplicated row inside the track).
 *   - Pauses on hover for readability + accessibility.
 */
export default function LatestAuditsTicker({ variant = "dark" }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      axios.get(`${API}/stats/latest-audits?limit=12`).then((r) => {
        if (!cancelled) setItems(r.data?.items || []);
      }).catch(() => {});
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!items.length) return null;

  const isDark = variant === "dark";
  const wrapperCls = isDark
    ? "bg-white/5 border border-white/10"
    : "bg-slate-50 border border-slate-200";
  const dotCls = isDark ? "text-emerald-400" : "text-emerald-600";
  const nameCls = isDark ? "text-white" : "text-slate-900";
  const metaCls = isDark ? "text-slate-400" : "text-slate-500";

  // Duplicate the row so CSS keyframes can loop seamlessly.
  const rendered = [...items, ...items];

  return (
    <div
      className={`relative overflow-hidden rounded-xl ${wrapperCls}`}
      data-testid="latest-audits-ticker"
    >
      <div className="flex items-center gap-2 px-4 pt-3 pb-1">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60"></span>
          <span className={`relative inline-flex rounded-full h-2 w-2 bg-emerald-400`}></span>
        </span>
        <Activity className={`w-3.5 h-3.5 ${dotCls}`} />
        <span className={`text-[11px] uppercase tracking-widest font-bold ${metaCls}`}>
          Práve prebieha audit
        </span>
      </div>

      <div className="relative overflow-hidden pb-3">
        <div
          className="flex gap-3 whitespace-nowrap animate-marquee hover:[animation-play-state:paused] will-change-transform"
          style={{ animationDuration: `${Math.max(rendered.length * 3, 30)}s` }}
          data-testid="latest-audits-track"
        >
          {rendered.map((it, i) => (
            <a
              key={`${it.url}-${i}`}
              href={it.url}
              className={`flex items-center gap-2 shrink-0 px-3 py-2 rounded-lg ${isDark ? "bg-black/30 hover:bg-black/50" : "bg-white hover:bg-slate-100"} border ${isDark ? "border-white/5" : "border-slate-200"} transition-colors`}
              data-testid={`ticker-item-${i}`}
            >
              <FlagEmoji country={it.country} />
              <span className={`text-sm font-semibold ${nameCls}`}>{it.name}</span>
              {it.city && it.kind === "firm" && (
                <span className={`text-xs ${metaCls}`}>· {it.city}</span>
              )}
              <span className={`text-[10px] uppercase tracking-wider ${metaCls} ml-1`}>
                {it.when}
              </span>
            </a>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes marquee-x {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation-name: marquee-x;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
        }
      `}</style>
    </div>
  );
}

function FlagEmoji({ country }) {
  const map = { SK: "🇸🇰", CZ: "🇨🇿", AT: "🇦🇹", HU: "🇭🇺", PL: "🇵🇱" };
  return <span className="text-base leading-none">{map[country] || "🏳️"}</span>;
}
